import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { formatCurrency } from '../lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  PiggyBank,
  AlertCircle,
  ArrowRight,
  FileText,
  DollarSign,
  CreditCard,
  Building,
  Clock,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Receipt,
  BookOpen,
  Calculator,
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';
import { motion } from 'framer-motion';

// Animation wrapper
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay, ease: 'easeOut' }}
    className={className}
  >
    {children}
  </motion.div>
);

// Compact KPI for accountant view
const CompactKPI = ({ label, value, delta, positive, currency }) => (
  <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
    <span className="text-sm text-[hsl(var(--muted-ink))]">{label}</span>
    <div className="flex items-center gap-2">
      <span className="font-semibold numbered">{formatCurrency(value, currency)}</span>
      {delta !== undefined && (
        <span className={`text-xs flex items-center ${
          positive ? 'text-emerald-600' : positive === false ? 'text-rose-600' : 'text-slate-500'
        }`}>
          {positive ? <ArrowUpRight size={12} /> : positive === false ? <ArrowDownRight size={12} /> : <Minus size={12} />}
          {Math.abs(delta || 0).toFixed(1)}%
        </span>
      )}
    </div>
  </div>
);

const DashboardDetailed = () => {
  const { company, currency, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [reconciliationStatus, setReconciliationStatus] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadAllData();
    
    const handleRefresh = () => loadAllData();
    window.addEventListener('transaction-created', handleRefresh);
    return () => window.removeEventListener('transaction-created', handleRefresh);
  }, []);

  const loadAllData = async () => {
    try {
      const [dashRes, cashFlowRes, accountsRes, receivablesRes, payablesRes, auditRes, reconRes] = await Promise.all([
        api.get('/dashboard'),
        api.get('/dashboard/cash-flow?months=6'),
        api.get('/accounts').catch(() => ({ data: [] })),
        api.get('/receivables').catch(() => ({ data: [] })),
        api.get('/payables').catch(() => ({ data: [] })),
        api.get('/audit').catch(() => ({ data: [] })),
        api.get('/reconciliation').catch(() => ({ data: [] }))
      ]);
      
      setDashboardData(dashRes.data);
      setCashFlowData(cashFlowRes.data);
      setAccounts(accountsRes.data || []);
      setReceivables(receivablesRes.data || []);
      setPayables(payablesRes.data || []);
      setAuditLogs(auditRes.data?.slice(0, 10) || []);
      setReconciliationStatus(reconRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const kpis = dashboardData?.kpis || {};
  const fmt = (v) => formatCurrency(v, currency);

  // Calculate totals
  const totalReceivables = useMemo(() => 
    receivables.filter(r => r.status !== 'paid').reduce((sum, r) => sum + (parseFloat(r.amount_due || 0) - parseFloat(r.amount_paid || 0)), 0)
  , [receivables]);

  const totalPayables = useMemo(() => 
    payables.filter(p => p.status !== 'paid').reduce((sum, p) => sum + (parseFloat(p.amount || 0) - parseFloat(p.amount_paid || 0)), 0)
  , [payables]);

  const unreconciledCount = useMemo(() => 
    reconciliationStatus.filter(r => r.status === 'unreconciled').length
  , [reconciliationStatus]);

  // AR Aging breakdown
  const arAging = useMemo(() => {
    const buckets = { current: 0, '30': 0, '60': 0, '90': 0 };
    const today = new Date();
    
    receivables.filter(r => r.status !== 'paid').forEach(r => {
      const dueDate = new Date(r.due_date);
      const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
      const amount = parseFloat(r.amount_due || 0) - parseFloat(r.amount_paid || 0);
      
      if (daysOverdue <= 0) buckets.current += amount;
      else if (daysOverdue <= 30) buckets['30'] += amount;
      else if (daysOverdue <= 60) buckets['60'] += amount;
      else buckets['90'] += amount;
    });
    
    return buckets;
  }, [receivables]);

  // Account balances by type
  const accountsByType = useMemo(() => {
    const grouped = { asset: [], liability: [], equity: [] };
    accounts.forEach(acc => {
      const type = acc.account_type || 'asset';
      if (grouped[type]) grouped[type].push(acc);
    });
    return grouped;
  }, [accounts]);

  return (
    <div className="space-y-4 pb-8" data-testid="dashboard-detailed">
      {/* Compact Header */}
      <FadeIn>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl">
          <div>
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
              Accounting Dashboard
            </h1>
            <p className="text-sm text-[hsl(var(--muted-ink))]">
              {company?.name} • As of {new Date().toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              <Clock size={12} className="mr-1" />
              Last updated: {new Date().toLocaleTimeString()}
            </Badge>
            <Button size="sm" variant="outline" onClick={loadAllData}>
              <RefreshCw size={14} className="mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </FadeIn>

      {/* KPI Summary Strip */}
      <FadeIn delay={0.05}>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          <CompactKPI label="Revenue" value={kpis.revenue?.value || 0} delta={kpis.revenue?.delta} positive={kpis.revenue?.positive} currency={currency} />
          <CompactKPI label="Expenses" value={kpis.expenses?.value || 0} delta={kpis.expenses?.delta} positive={kpis.expenses?.positive} currency={currency} />
          <CompactKPI label="Net P/L" value={kpis.net_profit?.value || 0} positive={kpis.net_profit?.positive} currency={currency} />
          <CompactKPI label="Cash" value={kpis.cash_position?.value || 0} currency={currency} />
          <CompactKPI label="AR Total" value={totalReceivables} currency={currency} />
          <CompactKPI label="AP Total" value={totalPayables} currency={currency} />
        </div>
      </FadeIn>

      {/* Main Content Tabs */}
      <FadeIn delay={0.1}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start bg-slate-100 dark:bg-slate-800 p-1 overflow-x-auto">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">Overview</TabsTrigger>
            <TabsTrigger value="accounts" className="text-xs sm:text-sm">Chart of Accounts</TabsTrigger>
            <TabsTrigger value="ar" className="text-xs sm:text-sm">AR Aging</TabsTrigger>
            <TabsTrigger value="journal" className="text-xs sm:text-sm">Journal Entries</TabsTrigger>
            <TabsTrigger value="reconciliation" className="text-xs sm:text-sm">Reconciliation</TabsTrigger>
            <TabsTrigger value="audit" className="text-xs sm:text-sm">Audit Log</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Cash Flow Summary */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Activity size={16} />
                    Cash Flow Summary (6 Months)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Period</TableHead>
                        <TableHead className="text-xs text-right">Inflow</TableHead>
                        <TableHead className="text-xs text-right">Outflow</TableHead>
                        <TableHead className="text-xs text-right">Net</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cashFlowData.slice(0, 6).map((row, i) => (
                        <TableRow key={i} className="text-sm">
                          <TableCell className="font-medium">{row.month}</TableCell>
                          <TableCell className="text-right text-emerald-600 numbered">{fmt(row.inflow || 0)}</TableCell>
                          <TableCell className="text-right text-rose-600 numbered">{fmt(row.outflow || 0)}</TableCell>
                          <TableCell className={`text-right font-medium numbered ${(row.inflow - row.outflow) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {fmt((row.inflow || 0) - (row.outflow || 0))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Status Indicators */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    Status Indicators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                      <FileText size={16} className="text-[hsl(var(--muted-ink))]" />
                      <span className="text-sm">Outstanding Invoices</span>
                    </div>
                    <Badge variant="outline">{receivables.filter(r => r.status !== 'paid').length}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                      <Receipt size={16} className="text-[hsl(var(--muted-ink))]" />
                      <span className="text-sm">Pending Bills</span>
                    </div>
                    <Badge variant="outline">{payables.filter(p => p.status !== 'paid').length}</Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                      <RefreshCw size={16} className="text-[hsl(var(--muted-ink))]" />
                      <span className="text-sm">Unreconciled Items</span>
                    </div>
                    <Badge variant={unreconciledCount > 0 ? 'destructive' : 'secondary'}>
                      {unreconciledCount}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-amber-500" />
                      <span className="text-sm">Overdue AR (90+ days)</span>
                    </div>
                    <span className="font-semibold text-rose-600 numbered">{fmt(arAging['90'])}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Chart of Accounts Tab */}
          <TabsContent value="accounts" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Assets */}
              <Card>
                <CardHeader className="pb-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-t-lg">
                  <CardTitle className="text-sm text-emerald-700 dark:text-emerald-400">Assets</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-64">
                    <Table>
                      <TableBody>
                        {accountsByType.asset.map((acc) => (
                          <TableRow key={acc.id} className="text-sm">
                            <TableCell>{acc.name}</TableCell>
                            <TableCell className="text-right font-medium numbered">
                              {fmt(acc.current_balance || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {accountsByType.asset.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-[hsl(var(--muted-ink))]">
                              No asset accounts
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Liabilities */}
              <Card>
                <CardHeader className="pb-2 bg-rose-50 dark:bg-rose-900/20 rounded-t-lg">
                  <CardTitle className="text-sm text-rose-700 dark:text-rose-400">Liabilities</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-64">
                    <Table>
                      <TableBody>
                        {accountsByType.liability.map((acc) => (
                          <TableRow key={acc.id} className="text-sm">
                            <TableCell>{acc.name}</TableCell>
                            <TableCell className="text-right font-medium numbered">
                              {fmt(acc.current_balance || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {accountsByType.liability.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-[hsl(var(--muted-ink))]">
                              No liability accounts
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Equity */}
              <Card>
                <CardHeader className="pb-2 bg-blue-50 dark:bg-blue-900/20 rounded-t-lg">
                  <CardTitle className="text-sm text-blue-700 dark:text-blue-400">Equity</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <ScrollArea className="h-64">
                    <Table>
                      <TableBody>
                        {accountsByType.equity.map((acc) => (
                          <TableRow key={acc.id} className="text-sm">
                            <TableCell>{acc.name}</TableCell>
                            <TableCell className="text-right font-medium numbered">
                              {fmt(acc.current_balance || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {accountsByType.equity.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-[hsl(var(--muted-ink))]">
                              No equity accounts
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AR Aging Tab */}
          <TabsContent value="ar" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calculator size={16} />
                  Accounts Receivable Aging Report
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <p className="text-xs text-emerald-600 font-medium">Current</p>
                    <p className="text-2xl font-bold numbered">{fmt(arAging.current)}</p>
                  </div>
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <p className="text-xs text-amber-600 font-medium">1-30 Days</p>
                    <p className="text-2xl font-bold numbered">{fmt(arAging['30'])}</p>
                  </div>
                  <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-xs text-orange-600 font-medium">31-60 Days</p>
                    <p className="text-2xl font-bold numbered">{fmt(arAging['60'])}</p>
                  </div>
                  <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-lg">
                    <p className="text-xs text-rose-600 font-medium">61+ Days</p>
                    <p className="text-2xl font-bold numbered">{fmt(arAging['90'])}</p>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead className="text-right">Amount Due</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {receivables.filter(r => r.status !== 'paid').slice(0, 10).map((r) => (
                      <TableRow key={r.id} className="text-sm">
                        <TableCell className="font-medium">{r.customer_name || 'Unknown'}</TableCell>
                        <TableCell>{r.invoice_number || '-'}</TableCell>
                        <TableCell>{new Date(r.due_date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right font-medium numbered">
                          {fmt((r.amount_due || 0) - (r.amount_paid || 0))}
                        </TableCell>
                        <TableCell>
                          <Badge variant={new Date(r.due_date) < new Date() ? 'destructive' : 'secondary'} className="text-xs">
                            {new Date(r.due_date) < new Date() ? 'Overdue' : 'Pending'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {receivables.filter(r => r.status !== 'paid').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[hsl(var(--muted-ink))]">
                          No outstanding receivables
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Journal Entries Tab */}
          <TabsContent value="journal" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <BookOpen size={16} />
                  Recent Journal Entries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData?.recent_transactions?.slice(0, 15).map((txn) => (
                      <TableRow key={txn.id} className="text-sm">
                        <TableCell>{new Date(txn.date).toLocaleDateString()}</TableCell>
                        <TableCell className="font-mono text-xs">{txn.reference || txn.id?.slice(0, 8)}</TableCell>
                        <TableCell>{txn.description || '-'}</TableCell>
                        <TableCell className="text-right numbered">
                          {txn.transaction_type === 'expense' ? fmt(txn.amount) : '-'}
                        </TableCell>
                        <TableCell className="text-right numbered">
                          {txn.transaction_type === 'revenue' ? fmt(txn.amount) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!dashboardData?.recent_transactions || dashboardData.recent_transactions.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-[hsl(var(--muted-ink))]">
                          No journal entries found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reconciliation Tab */}
          <TabsContent value="reconciliation" className="mt-4">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw size={16} />
                  Bank Reconciliation Status
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => window.location.href = '/reconciliation'}>
                  Open Reconciliation <ChevronRight size={14} className="ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {accounts.filter(a => a.account_subtype === 'bank').map((acc) => {
                    const reconItems = reconciliationStatus.filter(r => r.account_id === acc.id);
                    const unrecon = reconItems.filter(r => r.status === 'unreconciled').length;
                    
                    return (
                      <div key={acc.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <div className="flex items-center gap-3">
                          <Building size={18} className="text-[hsl(var(--muted-ink))]" />
                          <div>
                            <p className="font-medium text-sm">{acc.name}</p>
                            <p className="text-xs text-[hsl(var(--muted-ink))]">
                              Balance: {fmt(acc.current_balance || 0)}
                            </p>
                          </div>
                        </div>
                        <Badge variant={unrecon > 0 ? 'destructive' : 'secondary'}>
                          {unrecon > 0 ? `${unrecon} unreconciled` : 'Reconciled'}
                        </Badge>
                      </div>
                    );
                  })}
                  {accounts.filter(a => a.account_subtype === 'bank').length === 0 && (
                    <p className="text-center text-[hsl(var(--muted-ink))] py-4">No bank accounts configured</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="mt-4">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <FileText size={16} />
                  Recent Audit Activity
                </CardTitle>
                <Button size="sm" variant="outline" onClick={() => window.location.href = '/audit'}>
                  Full Audit Log <ChevronRight size={14} className="ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-80">
                  <div className="space-y-2">
                    {auditLogs.map((log, i) => (
                      <div key={log.id || i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                        <div className={`p-1.5 rounded ${
                          log.action === 'create' ? 'bg-emerald-100 text-emerald-600' :
                          log.action === 'update' ? 'bg-blue-100 text-blue-600' :
                          log.action === 'delete' ? 'bg-rose-100 text-rose-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {log.action === 'create' ? <CheckCircle2 size={14} /> :
                           log.action === 'delete' ? <XCircle size={14} /> :
                           <RefreshCw size={14} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{log.description || `${log.action} ${log.entity_type}`}</p>
                          <p className="text-xs text-[hsl(var(--muted-ink))]">
                            {log.user_name || 'System'} • {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                    {auditLogs.length === 0 && (
                      <p className="text-center text-[hsl(var(--muted-ink))] py-8">No audit activity</p>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </FadeIn>
    </div>
  );
};

export default DashboardDetailed;
